/**
 * EQ.app Toast Notification Component
 * 
 * Premium toast notifications with:
 * - Smooth slide-in animations (300ms ease-out)
 * - Glass morphism effect
 * - Brand-aligned colors
 * - Auto-dismiss with progress bar
 */

import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { 
  Check, 
  X, 
  AlertCircle, 
  Loader2, 
  RefreshCw,
  Save,
  Info,
  Sparkles
} from 'lucide-react';

// Toast types with EQ.app brand styling
const TOAST_TYPES = {
  success: {
    icon: Check,
    bg: 'bg-[#52dcb3]/10 border-[#52dcb3]/30',
    iconBg: 'bg-[#52dcb3] text-white',
    text: 'text-black',
    subtext: 'text-black/60',
    progress: 'bg-[#52dcb3]'
  },
  error: {
    icon: AlertCircle,
    bg: 'bg-red-50 border-red-200',
    iconBg: 'bg-red-500 text-white',
    text: 'text-black',
    subtext: 'text-black/60',
    progress: 'bg-red-500'
  },
  loading: {
    icon: Loader2,
    bg: 'bg-[#5a20ff]/5 border-[#5a20ff]/20',
    iconBg: 'bg-[#5a20ff] text-white',
    text: 'text-black',
    subtext: 'text-black/50',
    progress: 'bg-[#5a20ff]'
  },
  info: {
    icon: Info,
    bg: 'bg-blue-50 border-blue-200',
    iconBg: 'bg-blue-500 text-white',
    text: 'text-black',
    subtext: 'text-black/60',
    progress: 'bg-blue-500'
  },
  saved: {
    icon: Save,
    bg: 'bg-[#52dcb3]/10 border-[#52dcb3]/30',
    iconBg: 'bg-[#52dcb3] text-white',
    text: 'text-black',
    subtext: 'text-black/60',
    progress: 'bg-[#52dcb3]'
  },
  ai: {
    icon: Sparkles,
    bg: 'bg-gradient-to-r from-[#5a20ff]/5 to-[#52dcb3]/5 border-[#5a20ff]/20',
    iconBg: 'bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] text-white',
    text: 'text-black',
    subtext: 'text-black/50',
    progress: 'bg-gradient-to-r from-[#5a20ff] to-[#52dcb3]'
  }
};

// Individual Toast Component
function ToastItem({ toast, onDismiss, onRetry: _onRetry }) {
  const config = TOAST_TYPES[toast.type] || TOAST_TYPES.info;
  const Icon = config.icon;
  const isLoading = toast.type === 'loading';
  const [progress, setProgress] = useState(100);
  const duration = toast.duration || (toast.type === 'error' ? 6000 : 4000);
  
  // Progress bar countdown
  useEffect(() => {
    if (isLoading || toast.duration === 0) return;
    
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 50);
    
    return () => clearInterval(interval);
  }, [duration, isLoading, toast.duration]);
  
  return (
    <div 
      className={`
        relative overflow-hidden
        flex items-start gap-3 p-4 rounded-2xl border 
        bg-white/95 backdrop-blur-xl shadow-lg
        ${config.bg}
      `}
      style={{
        animation: 'eq-toast-enter 300ms cubic-bezier(0.16, 1, 0.3, 1)',
        maxWidth: '380px',
        fontFamily: "'Playfair Display', serif",
      }}
      role="alert"
    >
      {/* Icon */}
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${config.iconBg}`}>
        <Icon className={`w-4.5 h-4.5 ${isLoading ? 'animate-spin' : ''}`} />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        <p className={`text-sm font-semibold ${config.text}`}>
          {toast.title}
        </p>
        {toast.message && (
          <p className={`text-xs mt-0.5 ${config.subtext}`}>
            {toast.message}
          </p>
        )}
        
        {/* Retry button for errors */}
        {toast.type === 'error' && toast.onRetry && (
          <button 
            onClick={() => {
              toast.onRetry();
              onDismiss(toast.id);
            }}
            className="mt-2 flex items-center gap-1.5 text-xs font-medium text-[#5a20ff] hover:text-[#5a20ff]/80 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Try again
          </button>
        )}
      </div>
      
      {/* Dismiss button (not for loading) */}
      {!isLoading && (
        <button 
          onClick={() => onDismiss(toast.id)}
          className="p-1.5 rounded-lg hover:bg-black/5 transition-colors flex-shrink-0 -mr-1 -mt-1"
        >
          <X className="w-4 h-4 text-black/40" />
        </button>
      )}
      
      {/* Progress bar */}
      {!isLoading && toast.duration !== 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black/5">
          <div 
            className={`h-full transition-all duration-100 ease-linear ${config.progress}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      
      <style>{`
        @keyframes eq-toast-enter {
          from {
            opacity: 0;
            transform: translateX(100%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}

// Toast Container - renders at fixed position
function ToastContainer({ toasts, onDismiss, onRetry }) {
  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none">
      {toasts.map(toast => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem 
            toast={toast} 
            onDismiss={onDismiss}
            onRetry={onRetry}
          />
        </div>
      ))}
    </div>
  );
}

// Toast Context for global access
const ToastContext = createContext(null);

// Track recent toasts by dedupe key to prevent spam
const recentToastKeys = new Map();
const DEDUPE_WINDOW_MS = 3000;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  
  const addToast = useCallback((toast) => {
    // Dedupe: prevent duplicate toasts within window
    const dedupeKey = toast.dedupeKey || `${toast.type}-${toast.title}-${toast.message || ''}`;
    const now = Date.now();
    const lastShown = recentToastKeys.get(dedupeKey);
    
    if (lastShown && (now - lastShown) < DEDUPE_WINDOW_MS) {
      return null;
    }
    
    recentToastKeys.set(dedupeKey, now);
    
    // Clean up old keys
    if (recentToastKeys.size > 50) {
      const cutoff = now - DEDUPE_WINDOW_MS;
      for (const [key, time] of recentToastKeys.entries()) {
        if (time < cutoff) recentToastKeys.delete(key);
      }
    }
    
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newToast = { id, ...toast };
    
    setToasts(prev => {
      const updated = [...prev, newToast];
      return updated.slice(-3); // Max 3 toasts
    });
    
    // Auto-dismiss
    if (toast.type !== 'loading' && toast.duration !== 0) {
      const duration = toast.duration || (toast.type === 'error' ? 6000 : 4000);
      setTimeout(() => {
        dismissToast(id);
      }, duration);
    }
    
    return id;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  
  const updateToast = useCallback((id, updates) => {
    setToasts(prev => prev.map(t => 
      t.id === id ? { ...t, ...updates } : t
    ));
  }, []);
  
  // Convenience methods
  const toast = {
    success: (title, message, options = {}) => 
      addToast({ type: 'success', title, message, ...options }),
    
    error: (title, message, options = {}) => 
      addToast({ type: 'error', title, message, ...options }),
    
    loading: (title, message, options = {}) => 
      addToast({ type: 'loading', title, message, duration: 0, ...options }),
    
    info: (title, message, options = {}) => 
      addToast({ type: 'info', title, message, ...options }),
    
    saved: (title, message, options = {}) => 
      addToast({ type: 'saved', title, message, ...options }),
    
    ai: (title, message, options = {}) => 
      addToast({ type: 'ai', title, message, ...options }),
    
    dismiss: dismissToast,
    update: updateToast,
  };
  
  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer 
        toasts={toasts} 
        onDismiss={dismissToast}
      />
    </ToastContext.Provider>
  );
}

// Hook to use toast notifications
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    return {
      success: () => {},
      error: () => {},
      loading: () => {},
      info: () => {},
      saved: () => {},
      ai: () => {},
      dismiss: () => {},
      update: () => {},
    };
  }
  return context;
}

export default ToastContainer;
